/**
 * 渲染插件。
 * 在引擎初始化时创建 RenderPipeline 并注册为服务，
 * 每帧以 priority=100 确保在所有其他 Plugin 之后执行渲染。
 */

import type { GraphicsRequirements } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { Plugin } from '@/shared/types/plugin'
import type { ViewFrameContext } from '@/shared/types/view'
import { RenderPipeline } from './RenderPipeline'

/** 渲染插件：管理 RenderPipeline 生命周期，驱动每帧渲染 */
export class RenderPlugin implements Plugin {
  /** 插件名称 */
  readonly name = 'render'
  /** priority=100 确保在所有其他 Plugin 的 onUpdate 之后执行 */
  readonly priority = 100
  /** 仅依赖统一 surface/View 调度；具体 contributor 在动态注册时二次门禁。 */
  readonly graphicsRequirements: GraphicsRequirements = Object.freeze({
    backends: Object.freeze(['webgl2', 'webgpu'] as const),
    capabilities: Object.freeze(['surfaceRender', 'multiView'] as const),
  })
  /** 对外提供的服务名列表 */
  readonly provides = ['RenderPipeline'] as const

  /** 渲染管线实例，onInit 中创建 */
  private pipeline!: RenderPipeline

  /**
   * 创建 RenderPipeline 并注册到服务容器。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    const renderInvalidation =
      typeof context.hasService === 'function' && context.hasService('RenderInvalidationService')
        ? context.getService('RenderInvalidationService')
        : null
    this.pipeline = new RenderPipeline(context.events, renderInvalidation, context.graphics)
    context.registerService('RenderPipeline', this.pipeline)
  }

  /** 启动阶段，无需额外操作 */
  onStart(): void {}

  /**
   * 多视口渲染入口：每个 view 分别执行一次渲染管线。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    this.pipeline?.execute(frame)
  }

  /** 销毁时释放渲染管线资源 */
  onDestroy(): void {
    this.pipeline?.dispose()
  }
}
